// Full-document render of the site-search results page: PageSkeleton wrapping
// the search bar (with the inline x-close clear icon), the optional
// suggested-query, the dynamic promotional StreamField results, the result
// list, pagination, the GTM `searchResults` push, and the inlined
// customer-service info block.
//
// The dynamic results are the matching DynamicSearchSnippets' StreamField
// blocks rendered natively through the shared block components -- each wrapped
// in `w-block-{type} block-{type}` to match Wagtail's StreamBlock
// `render_basic` markup.
import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { ColumnsCarouselBlock } from "tsi-common-react/src/components/ColumnsCarouselBlock/index";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { jt, t } from "ttag";

import { templates } from "@reactivated";
import type {
    ColumnsCarouselBlock as ColumnsCarouselBlockValue,
    SharedFAQsBlock as FAQsBlockValue,
    PromotionBlock as PromotionBlockValue,
} from "@reactivated";

import {
    StreamField,
    type StreamFieldBlockMap,
} from "@thelabnyc/thelabui/src/components/StreamField";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CustomerServiceInfoBlock } from "./CustomerServiceInfoBlockTemplate";

// Mutable reactivated props (not Immutable<>): composes PageSkeleton.
type IProps = templates.SearchResultsPageTemplate;

// The RichSearchResults StreamField serializes each block through a distinct
// reactivated occurrence whose generated value type diverges from the shared
// block components' canonical types (a known per-@template codegen quirk -- the
// runtime payload is identical). We render through the shared components (typed
// to their canonical values) and bridge the snippet value at the single
// StreamField boundary below.
type DynamicStreamValue = readonly {
    readonly id: string;
    readonly type: "promotion_block" | "faqs_block" | "columns_carousel_block";
    readonly value:
        | Immutable<PromotionBlockValue>
        | Immutable<FAQsBlockValue>
        | Immutable<ColumnsCarouselBlockValue>;
}[];

// Per-block map. Each block gets the `w-block-{type} block-{type}` wrapper
// Wagtail's StreamBlock `render_basic` emits.
const dynamicComponents: StreamFieldBlockMap<DynamicStreamValue> = {
    promotion_block: (block) => (
        <div className="w-block-promotion_block block-promotion_block">
            <PromotionBlock
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- narrows the DynamicStreamValue union to this block's canonical type; only looks redundant in CI, where the site's @reactivated types aren't generated before lint runs (so "@reactivated" resolves to `any` there)
                value={block.value as Immutable<PromotionBlockValue>}
            />
        </div>
    ),
    faqs_block: (block) => (
        <div className="w-block-faqs_block block-faqs_block">
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- see promotion_block above */}
            <FAQsBlock value={block.value as Immutable<FAQsBlockValue>} />
        </div>
    ),
    columns_carousel_block: (block) => (
        <div className="w-block-columns_carousel_block block-columns_carousel_block">
            <ColumnsCarouselBlock
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- see promotion_block above
                value={block.value as Immutable<ColumnsCarouselBlockValue>}
            />
        </div>
    ),
};

const DynamicResults = (props: {
    snippets: IProps["dynamic_results_top"];
}): React.JSX.Element => (
    <div className="search-results__dynamic">
        {props.snippets.map((snippet, i) => (
            <StreamField<DynamicStreamValue>
                key={i}
                tagName={React.Fragment}
                value={
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- bridges the RichSearchResults StreamField's per-occurrence codegen type to the canonical DynamicStreamValue; see promotion_block above for why CI's lint sees this as redundant
                    snippet.components.value as unknown as DynamicStreamValue
                }
                components={dynamicComponents}
            />
        ))}
    </div>
);

// The GTM `searchResults` dataLayer push (client-side; gated on a query).
const SearchResultsTracker = (props: {
    numResults: number;
    query: string;
}): null => {
    useEffect(() => {
        const win = getWindow();
        if (!win) {
            return;
        }
        const w = win as unknown as { dataLayer?: Record<string, unknown>[] };
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
            event: "searchResults",
            numSearchResults: props.numResults,
            search_query: props.query,
        });
    }, [props.numResults, props.query]);
    return null;
};

export const Template = (props: IProps): React.JSX.Element => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const q = props.search_query;
    const clearSearch = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };
    const onClearSearchKeyDown = (
        event: React.KeyboardEvent<SVGSVGElement>,
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            clearSearch();
        }
    };
    // ttag's `jt` extraction forbids inline JSX elements in the `${...}`
    // interpolation, so build the emphasized elements first.
    const queryEm = <em key="query">{q}</em>;
    const suggestionLink = props.suggested_query ? (
        <a
            key="suggestion"
            href={`${props.search_url}?query=${props.suggested_query.query_text}`}
        >
            <em>{props.suggested_query.query_text}</em>
        </a>
    ) : null;
    const cityLink = props.location_result ? (
        <a
            key="city"
            className="location-link"
            href={props.location_result.retailer_url}
        >
            {props.location_result.city_name}
        </a>
    ) : null;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-capped-width" style={{ paddingTop: "150px" }}>
                <div className="search-results">
                    <div className="search-results__search-bar">
                        <form
                            className="search-results__search-form"
                            action={props.search_url}
                            method="get"
                        >
                            <input
                                type="text"
                                name="query"
                                defaultValue={q}
                                ref={inputRef}
                                className="search-results__search-input"
                                placeholder={t`Search`}
                            />
                            <svg
                                className="search-results__clear-icon"
                                role="button"
                                tabIndex={0}
                                aria-label={t`Clear search`}
                                xmlns="http://www.w3.org/2000/svg"
                                xmlSpace="preserve"
                                viewBox="0 0 43 43.6"
                                onClick={clearSearch}
                                onKeyDown={onClearSearchKeyDown}
                            >
                                <path d="M1.4 43.2c-.2 0-.7 0-1-.2-.5-.5-.5-1.2 0-1.8L40.9.7c.5-.5 1.2-.5 1.8 0 .5.5.5 1.2 0 1.8L2.1 42.9c-.2.3-.5.3-.7.3" />
                                <path d="M41.9 43.2c-.2 0-.8 0-1-.2L.3 2.4C-.2 1.9-.2 1.2.3.6.8.1 1.5.1 2.1.6l40.5 40.5c.5.5.5 1.2 0 1.8 0 .3-.5.3-.7.3" />
                            </svg>
                        </form>
                        {props.suggested_query && (
                            <div className="search-results__suggested-query">
                                <p>{jt`Showing results for ${queryEm}`}</p>
                                <p
                                    data-suggestion-score={
                                        props.suggested_query.score
                                    }
                                >
                                    {jt`Did you mean: ${suggestionLink}`}
                                </p>
                            </div>
                        )}
                    </div>

                    <DynamicResults snippets={props.dynamic_results_top} />

                    <ul className="search-results__responses">
                        {props.has_any_results ? (
                            props.results.map((result, i) => (
                                <li
                                    key={i}
                                    className="search-result"
                                    data-score={result.score}
                                >
                                    <div className="search-result__image">
                                        <a href={result.url}>
                                            <img
                                                src={result.image_url}
                                                alt={result.image_alt}
                                                title={result.image_alt}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </a>
                                    </div>
                                    <div className="search-result__info">
                                        <h4 className="search-result__info__name">
                                            <a href={result.url}>
                                                {result.title}
                                            </a>
                                        </h4>
                                        <div
                                            className="search-result__info__description"
                                            dangerouslySetInnerHTML={{
                                                __html: result.description_html,
                                            }}
                                        />
                                    </div>
                                </li>
                            ))
                        ) : q ? (
                            <li className="search-result--default">
                                {t`No results found`}
                            </li>
                        ) : (
                            <li className="search-result--default">
                                {t`Please type something into the search box`}
                            </li>
                        )}
                        {props.location_result && (
                            <li className="search-result location-search">
                                <div className="search-result__image">
                                    <a href="#">
                                        <img
                                            src={
                                                props.location_result.image_url
                                            }
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </a>
                                </div>
                                <div className="search-result__info">
                                    <h4 className="search-result__info__name">
                                        {jt`Are you looking for stores in ${cityLink}?`}
                                    </h4>
                                    <div className="search-result__info__description"></div>
                                </div>
                            </li>
                        )}
                    </ul>

                    <DynamicResults snippets={props.dynamic_results_bottom} />

                    <div className="search-results__pagination">
                        <div className="search-results__pagination__links search-results__pagination__links--previous">
                            {props.pagination.has_previous && (
                                <>
                                    <a href={`?query=${q}&page=1`}>
                                        &laquo; {t`First`}
                                    </a>
                                    &nbsp;|&nbsp;
                                    <a
                                        href={`?query=${q}&page=${props.pagination.previous_page_number}`}
                                    >
                                        {t`Previous`}
                                    </a>
                                </>
                            )}
                        </div>

                        <div className="search-results__pagination__links search-results__pagination__current">
                            {props.pagination_label}
                        </div>

                        <div className="search-results__pagination__links search-results__pagination__links--next">
                            {props.pagination.has_next && (
                                <>
                                    <a
                                        href={`?query=${q}&page=${props.pagination.next_page_number}`}
                                    >
                                        {t`Next`}
                                    </a>
                                    &nbsp;|&nbsp;
                                    <a
                                        href={`?query=${q}&page=${props.pagination.num_pages}`}
                                    >
                                        {t`Last`} &raquo;
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                    {props.track_search && (
                        <SearchResultsTracker
                            numResults={props.num_search_results}
                            query={q}
                        />
                    )}
                </div>
            </div>

            <CustomerServiceInfoBlock {...props.customer_service} />
        </PageSkeleton>
    );
};
